<template>
  <div>
    <b-card>
      <b-table :items="pauses" :fields="fields" striped hover>
        <template #cell(reason)="data">
          {{ $t(`partner.pauses.reasons.${data.value}`) }}
        </template>
        <template #cell(actions)="row">
          <b-button size="sm" @click="editPause(row.item)" class="mr-1">
            <i class="material-icons">edit</i>
          </b-button>
          <b-button size="sm" variant="danger" @click="deletePause(row.item)">
            <i class="material-icons">delete</i>
          </b-button>
        </template>
        <template #cell(lastModifiedByContact)="data">
          {{ data.value?.firstName || '' }} {{ data.value?.lastName || '-' }}
        </template>
      </b-table>
      <b-button variant="primary" @click="addPause">
        {{ $t('partner.pauses.add') }}
      </b-button>
    </b-card>

    <b-pagination
      v-if="totalRows > perPage"
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
      class="my-3"
    />
    <div v-if="currentPageCount" class="text-center text-muted">
      <small>{{ currentPageCount }} / {{ totalRows }}</small>
    </div>

    <b-modal
      id="pause-modal"
      :title="modalTitle"
      ok-title="Save"
      @ok="validateAndSave"
      @cancel="resetModal"
      @hidden="clearValidationError"
    >
      <b-alert v-if="validationError" variant="danger" show>
        {{ validationError }}
      </b-alert>
      <b-form-group :label="`${$t('partner.pauses.start')}*`">
        <b-form-datepicker v-model="currentPause.startDate" />
      </b-form-group>
      <b-form-group :label="`${$t('partner.pauses.end')}*`">
        <b-form-datepicker v-model="currentPause.endDate" />
      </b-form-group>
      <b-form-group :label="`${$t('partner.pauses.reason')}*`">
        <multiselect
          v-model="currentPause.reason"
          :options="reasonsOptions"
          :placeholder="$t('partner.pauses.reason')"
          track-by="value"
          label="label"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { RequestQueryBuilder, CondOperator } from '@dataui/crud-request';
import { mapGetters } from 'vuex';
import { PartnerPauseReasonEnum } from '@gid/models';

export default {
  name: 'PartnerOnPause',
  props: {
    partner: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pauses: [],
      currentPause: { id: null, startDate: null, endDate: null, reason: null },
      modalTitle: '',
      fields: [
        { key: 'startDate', label: this.$t('partner.pauses.start') },
        { key: 'endDate', label: this.$t('partner.pauses.end') },
        { key: 'reason', label: this.$t('partner.pauses.reason') },
        {
          key: 'lastModifiedByContact',
          label: this.$t('partner.pauses.updated_by'),
        },
        { key: 'actions', label: 'Actions' },
      ],
      reasons: Object.values(PartnerPauseReasonEnum).map((key) => key),
      validationError: '',
      // Pagination
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      currentPageCount: null,
    };
  },
  computed: {
    ...mapGetters(['user']),
    reasonsOptions() {
      return Object.values(PartnerPauseReasonEnum).map((key) => ({
        value: key,
        label: this.$t(`partner.pauses.reasons.${key}`),
      }));
    },
  },
  watch: {
    partner: {
      immediate: true,
      handler() {
        this.loadPauses();
      },
    },
    currentPage() {
      this.loadPauses();
    },
  },
  methods: {
    async loadPauses() {
      if (!this.partner?.account?.id) return;

      const query = RequestQueryBuilder.create({
        filter: {
          field: 'partner.id',
          operator: CondOperator.EQUALS,
          value: this.partner.account.id,
        },
        page: this.currentPage,
        limit: this.perPage,
      }).query();

      try {
        const response = await axios.get(
          `/data-api/entity/partner-on-pause?${query}`,
        );
        this.pauses = response.data.data || response.data;
        this.totalRows = response.data.total || 0;
        this.currentPageCount = response.data.count || 0;
      } catch (error) {
        this.showError();
      }
    },
    addPause() {
      this.currentPause = {
        id: null,
        startDate: null,
        endDate: null,
        reason: null,
      };
      this.modalTitle = this.$t('partner.pauses.add');
      this.$bvModal.show('pause-modal');
    },
    editPause(pause) {
      this.currentPause = {
        id: pause.id,
        startDate: pause.startDate,
        endDate: pause.endDate,
        reason: {
          value: pause.reason,
          label: this.$t(`partner.pauses.reasons.${pause.reason}`),
        },
      };
      this.modalTitle = this.$t('partner.pauses.edit');
      this.$bvModal.show('pause-modal');
    },
    async savePause() {
      const data = {
        startDate: new Date(this.currentPause.startDate),
        endDate: new Date(this.currentPause.endDate),
        reason: this.currentPause.reason.value,
        partner: { id: this.partner.account.id },
      };

      try {
        if (this.currentPause.id) {
          await axios.put(
            `/data-api/entity/partner-on-pause/${this.currentPause.id}`,
            data,
          );
          this.$bvToast.toast(this.$t('partner.pauses.success_update'), {
            variant: 'success',
          });
        } else {
          await axios.post(`/data-api/entity/partner-on-pause`, data);
          this.$bvToast.toast(this.$t('partner.pauses.success_add'), {
            variant: 'success',
          });
        }
        this.loadPauses();
        this.$emit('update');
      } catch (error) {
        this.showError();
      }
    },
    async deletePause(pause) {
      if (!confirm(this.$t('partner.pauses.confirm_delete'))) return;

      try {
        await axios.delete(`/data-api/partner-on-pause/${pause.id}`);
        this.$bvToast.toast(this.$t('partner.pauses.success_delete'), {
          variant: 'success',
        });
        this.loadPauses();
        this.$emit('update');
      } catch (error) {
        this.showError();
      }
    },
    resetModal() {
      this.currentPause = {
        start: null,
        endDate: null,
        reason: null,
      };
    },
    showError() {
      this.$bvToast.toast(this.$t('partner.pauses.error'), {
        variant: 'danger',
      });
    },
    validateAndSave(bvModalEvent) {
      this.validationError = '';
      if (
        !this.currentPause.startDate ||
        !this.currentPause.endDate ||
        !this.currentPause.reason?.value
      ) {
        this.validationError = this.$t('partner.pauses.fields_required');
        bvModalEvent.preventDefault();
        return;
      }

      // Validate that start date is not greater than end date
      if (
        new Date(this.currentPause.startDate) >
        new Date(this.currentPause.endDate)
      ) {
        this.validationError = this.$t('partner.pauses.invalid_date_range');
        bvModalEvent.preventDefault();
        return;
      }

      this.savePause();
    },
    clearValidationError() {
      this.validationError = '';
    },
  },
};
</script>
